function normalizeActionLabel(actionLabel: string): string {
  return actionLabel
    .trim()
    .replace(/\.\.\.$/, '')
    .replace(/…$/, '')
    .replace(/^yes,\s*/i, '')
    .replace(/!+$/g, '')
    .trim()
}

const restLower = (rest: string) => rest.toLowerCase()

/**
 * Derives button loading copy from the primary action label (save, confirm, etc.).
 * Examples: "Save Country" → "Saving country...", "Delete Role" → "Deleting role..."
 */
export function actionLoadingText(actionLabel: string): string {
  const label = normalizeActionLabel(actionLabel)

  const rules: Array<{ pattern: RegExp; format: (rest: string) => string }> = [
    { pattern: /^leave without saving$/i, format: () => 'Leaving without saving...' },
    { pattern: /^confirm routing change$/i, format: () => 'Changing routing...' },
    { pattern: /^start impersonation$/i, format: () => 'Starting impersonation...' },
    { pattern: /^end session$/i, format: () => 'Ending session...' },
    { pattern: /^close$/i, format: () => 'Closing...' },
    { pattern: /^connect to (.+)$/i, format: (r) => `Connecting to ${restLower(r)}...` },
    { pattern: /^request change$/i, format: () => 'Requesting change...' },
    { pattern: /^regenerate\s+(.+)$/i, format: (r) => `Regenerating ${restLower(r)}...` },
    { pattern: /^regenerate$/i, format: () => 'Regenerating...' },
    { pattern: /^delete\s+(.+)$/i, format: (r) => `Deleting ${restLower(r)}...` },
    { pattern: /^delete$/i, format: () => 'Deleting...' },
    { pattern: /^remove\s+(.+)$/i, format: (r) => `Removing ${restLower(r)}...` },
    { pattern: /^remove$/i, format: () => 'Removing...' },
    { pattern: /^restore\s+(.+)$/i, format: (r) => `Restoring ${restLower(r)}...` },
    { pattern: /^restore$/i, format: () => 'Restoring...' },
    { pattern: /^block number$/i, format: () => 'Blocking number...' },
    { pattern: /^block\s+(.+)$/i, format: (r) => `Blocking ${restLower(r)}...` },
    { pattern: /^block$/i, format: () => 'Blocking...' },
    { pattern: /^deploy to everything$/i, format: () => 'Deploying to everything...' },
    {
      pattern: /^unblock\s*(.*)$/i,
      format: (r) => (r ? `Unblocking ${restLower(r)}...` : 'Unblocking...')
    },
    { pattern: /^disable\s+(.+)$/i, format: (r) => `Disabling ${restLower(r)}...` },
    { pattern: /^enable\s+(.+)$/i, format: (r) => `Enabling ${restLower(r)}...` },
    { pattern: /^deploy to (.+)$/i, format: (r) => `Deploying to ${restLower(r)}...` },
    { pattern: /^deploy$/i, format: () => 'Deploying...' },
    { pattern: /^suspend\s+(.+)$/i, format: (r) => `Suspending ${restLower(r)}...` },
    { pattern: /^reactivate$/i, format: () => 'Reactivating...' },
    { pattern: /^purchas(e|ing)$/i, format: () => 'Purchasing...' },
    { pattern: /^save\s+(.+)$/i, format: (r) => `Saving ${restLower(r)}...` },
    { pattern: /^add\s+(.+)$/i, format: (r) => `Adding ${restLower(r)}...` },
    { pattern: /^create\s+(.+)$/i, format: (r) => `Creating ${restLower(r)}...` },
    { pattern: /^register\s+(.+)$/i, format: (r) => `Registering ${restLower(r)}...` },
    { pattern: /^update\s+(.+)$/i, format: (r) => `Saving ${restLower(r)}...` },
    { pattern: /^generate\s+(.+)$/i, format: (r) => `Generating ${restLower(r)}...` },
    { pattern: /^apply\s+(.+)$/i, format: (r) => `Applying ${restLower(r)}...` },
    { pattern: /^set\s+(.+)$/i, format: (r) => `Setting ${restLower(r)}...` },
    { pattern: /^bulk\s+update$/i, format: () => 'Saving bulk update...' },
    { pattern: /^activate\s+(.+)$/i, format: (r) => `Activating ${restLower(r)}...` },
    { pattern: /^confirm$/i, format: () => 'Confirming...' },
    { pattern: /^import\s+(.+)$/i, format: (r) => `Importing ${restLower(r)}...` },
    { pattern: /^send\s+(.+)$/i, format: (r) => `Sending ${restLower(r)}...` }
  ]

  for (const { pattern, format } of rules) {
    const match = label.match(pattern)
    if (match) {
      return format(match[1] ?? '')
    }
  }

  return 'Loading...'
}
